
import { Helmet } from 'react-helmet-async';

interface SEOHelmetProps {
  title: string;
  description: string;
  keywords: string;
  author: string;
  robots: string;
  canonical: string;
  preloadImages: string[];
  alternateLanguages: { hreflang: string; href: string }[];
  articleData?: {
    datePublished?: string;
    dateModified?: string;
    author?: string;
    section?: string;
  };
  localBusiness?: {
    city?: string;
    region?: string;
    postalCode?: string;
  };
}

const SEOHelmet = ({
  title,
  description,
  keywords,
  author,
  robots,
  canonical,
  preloadImages,
  alternateLanguages,
  articleData,
  localBusiness
}: SEOHelmetProps) => {
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta name="robots" content={robots} />
      <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="bingbot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      
      {/* Enhanced viewport and mobile optimization */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      <meta name="format-detection" content="telephone=no" />
      
      {/* Enhanced security and performance */}
      <meta name="referrer" content="origin-when-cross-origin" />
      <meta httpEquiv="X-Content-Type-Options" content="nosniff" />
      <meta httpEquiv="X-Frame-Options" content="DENY" />
      
      <link rel="canonical" href={canonical} />
      
      {/* Preload critical images */}
      {preloadImages.map((imageUrl, index) => (
        <link key={index} rel="preload" as="image" href={imageUrl} />
      ))}
      
      {/* Alternate language versions */}
      {alternateLanguages.map((lang, index) => (
        <link key={index} rel="alternate" hrefLang={lang.hreflang} href={lang.href} />
      ))}
      
      {/* Article specific meta tags */}
      {articleData?.datePublished && (
        <meta property="article:published_time" content={articleData.datePublished} />
      )}
      {articleData?.dateModified && (
        <meta property="article:modified_time" content={articleData.dateModified} />
      )}
      {articleData?.author && (
        <meta property="article:author" content={articleData.author} />
      )}
      {articleData?.section && (
        <meta property="article:section" content={articleData.section} />
      )}
      
      {/* Additional SEO meta tags */}
      <meta name="theme-color" content="#22c55e" />
      <meta name="msapplication-TileColor" content="#22c55e" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      
      {/* Local Business specific meta tags */}
      {localBusiness?.city && (
        <meta name="geo.placename" content={localBusiness.city} />
      )}
      {localBusiness?.region && (
        <meta name="geo.region" content={localBusiness.region} />
      )}
      <meta name="geo.position" content="44.3500;2.5833" />
      <meta name="ICBM" content="44.3500, 2.5833" />
    </Helmet>
  );
};

export default SEOHelmet;
